/**
 * Typed accessors for the `/kits/*` Backend_API routes.
 */

import { useMemo } from 'react';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';
import type {
  AddKitItemsRequest,
  AddKitItemsResponse,
  CompleteKitResponse,
  CreateKitRequest,
  KitDetail,
  KitStatus,
  ListKitsResponse,
  ReorderKitItemsRequest,
  UpdateKitRequest,
} from '../shared/types';

export interface ListKitsFilters {
  status?: KitStatus;
  cursor?: string;
  limit?: number;
}

function listFiltersToQuery(
  filters: ListKitsFilters = {},
): Record<string, string | number | undefined> {
  return {
    status: filters.status,
    cursor: filters.cursor,
    limit: filters.limit,
  };
}

export function useKitsApi() {
  const api = useApi();
  return useMemo(
    () => ({
      list: (filters: ListKitsFilters = {}): Promise<ApiResult<ListKitsResponse>> =>
        api.request<ListKitsResponse>('/kits', {
          method: 'GET',
          query: listFiltersToQuery(filters),
        }),
      create: (body: CreateKitRequest = {}): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>('/kits', { method: 'POST', body }),
      get: (kitId: string): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>(`/kits/${encodeURIComponent(kitId)}`, { method: 'GET' }),
      deleteKit: (kitId: string): Promise<ApiResult<void>> =>
        api.request<void>(`/kits/${encodeURIComponent(kitId)}`, { method: 'DELETE' }),
      update: (kitId: string, body: UpdateKitRequest): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>(`/kits/${encodeURIComponent(kitId)}`, {
          method: 'PATCH',
          body,
        }),
      addItems: (
        kitId: string,
        body: AddKitItemsRequest,
      ): Promise<ApiResult<AddKitItemsResponse>> =>
        api.request<AddKitItemsResponse>(`/kits/${encodeURIComponent(kitId)}/items`, {
          method: 'POST',
          body,
        }),
      removeItem: (kitId: string, cardId: string): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>(
          `/kits/${encodeURIComponent(kitId)}/items/${encodeURIComponent(cardId)}`,
          { method: 'DELETE' },
        ),
      reorderItems: (kitId: string, body: ReorderKitItemsRequest): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>(`/kits/${encodeURIComponent(kitId)}/items/order`, {
          method: 'PUT',
          body,
        }),
      complete: (kitId: string): Promise<ApiResult<CompleteKitResponse>> =>
        api.request<CompleteKitResponse>(`/kits/${encodeURIComponent(kitId)}/complete`, {
          method: 'POST',
        }),
      reopen: (kitId: string): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>(`/kits/${encodeURIComponent(kitId)}/reopen`, {
          method: 'POST',
        }),
      cancel: (kitId: string): Promise<ApiResult<KitDetail>> =>
        api.request<KitDetail>(`/kits/${encodeURIComponent(kitId)}/cancel`, {
          method: 'POST',
        }),
      retryJob: (kitId: string, jobId: string): Promise<ApiResult<CompleteKitResponse>> =>
        api.request<CompleteKitResponse>(
          `/kits/${encodeURIComponent(kitId)}/jobs/${encodeURIComponent(jobId)}/retry`,
          { method: 'POST' },
        ),
    }),
    [api],
  );
}
